/**
 * §H5 change — "epoch selects (from available land-cover versions; default 2020→2021) ·
 * extent (country scope | AOI)"; result "transition matrix table (diagonal tinted
 * brand-tint; off-diagonal >2.5% tinted amber), net-change table (± coloured with km²)".
 *
 * **The epochs come from §D5**, not from a route of this tool's own: `/layers` already
 * carries `epoch` and is already access-filtered, so `epochRasters` is the whole catalogue
 * of epochs this reader may cross-tabulate (T6). Two selects over one list, defaulting to
 * the oldest and the newest, which today is exactly 2020 → 2021.
 *
 * **Both epochs travel or neither.** §H5 refuses one alone with a sentence; sending both
 * always is what keeps that refusal impossible from this form.
 *
 * `coverage.pct` can read a little over 100 — whole masked pixels against an exact
 * spheroidal polygon area (T6's parked note) — so the panel prints "100 %" for anything at
 * or above it rather than the honest-but-alarming "100.4 % covered".
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { aoiRing, useToolRun, type ChangeResult } from '../../../lib/analysis';
import { fmtKm2, fmtNum } from '../../../lib/format';
import { Icon } from '../../../lib/icons';
import { COUNTRY_NAMES, useAppStore } from '../../../lib/state';
import { toast } from '../../../lib/toast';
import type { ToolProps } from '../AnalysisDock';
import { ToolFrame } from '../ToolFrame';
import { ResultBox, ResultNote } from '../ResultBox';
import { epochRasters, useAnalysisLayers } from '../pickers';

/** §H5: "off-diagonal >2.5% tinted amber". */
export const WARM_THRESHOLD = 2.5;

export function Change({ onBack }: ToolProps) {
  const { t } = useTranslation();
  const country = useAppStore((s) => s.country);
  const scope = useAppStore((s) => s.scope);
  const aoi = useAppStore((s) => s.aoi);

  const layers = useAnalysisLayers();
  const epochs = useMemo(() => epochRasters(layers), [layers]);
  const [epochA, setEpochA] = useState('');
  const [epochB, setEpochB] = useState('');
  const [extent, setExtent] = useState<'scope' | 'aoi'>('scope');
  const run = useToolRun<ChangeResult>('change');

  const a = epochA || epochs[0]?.id || '';
  const b = epochB || epochs[epochs.length - 1]?.id || '';

  const scopeLabel = scope
    ? scope.name
    : country === 'ALL'
      ? t('analysis.change.programme')
      : t(`country.${country}`, COUNTRY_NAMES[country]);

  const submit = () => {
    if (!a || !b) {
      toast(t('analysis.change.noEpochs'));
      return;
    }
    if (a === b) {
      toast(t('analysis.change.sameEpoch'));
      return;
    }
    run.run({
      epoch_a: a,
      epoch_b: b,
      ...(extent === 'aoi' && aoi
        ? { aoi_polygon: aoiRing(aoi.pts) }
        : scope
          ? { unit_id: scope.unitId }
          : country === 'ALL'
            ? {}
            : { country }),
    });
  };

  const result = run.result;
  const classes = result?.matrix.classes ?? [];

  return (
    <ToolFrame
      tool="change"
      onBack={onBack}
      phase={run.phase}
      progress={run.progress}
      error={run.error}
      cached={run.cached}
      result={
        result ? (
          <>
            <ResultBox
              title={t('analysis.change.matrix', {
                a: result.epochs.a.year ?? '',
                b: result.epochs.b.year ?? '',
              })}
              scroll
            >
              <table className="mini">
                <thead>
                  <tr>
                    <th>
                      {result.epochs.a.year} ↓ / {result.epochs.b.year} →
                    </th>
                    {classes.map((c) => (
                      <th key={c.value} className="num">
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {classes.map((rowClass, i) => (
                    <tr key={rowClass.value}>
                      <td>
                        <b>{rowClass.label}</b>
                      </td>
                      {classes.map((colClass, j) => {
                        const value = result.matrix.rows[i]?.[j] ?? 0;
                        const tint =
                          i === j ? 'mini-cell-diag' : value > WARM_THRESHOLD ? 'mini-cell-warm' : '';
                        return (
                          <td key={colClass.value} className={`num ${tint}`.trim()}>
                            {fmtNum(value, 1)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </ResultBox>

            <ResultBox
              title={t('analysis.change.net', {
                a: result.epochs.a.year ?? '',
                b: result.epochs.b.year ?? '',
              })}
            >
              <table className="mini">
                <tbody>
                  {result.net.map((row) => (
                    <tr key={row.class}>
                      <td>
                        <span className="status-dot" style={{ background: row.color }} />
                        {row.label}
                      </td>
                      <td className={`num ${row.delta_pct >= 0 ? 'delta-up' : 'delta-down'}`}>
                        {row.delta_pct > 0 ? '+' : ''}
                        {fmtNum(row.delta_pct, 1)}%
                      </td>
                      <td className="num" style={{ color: 'var(--ink-3)' }}>
                        {fmtKm2(Math.abs(row.km2), 1)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ResultBox>

            <ResultNote>
              {t('analysis.change.coverage', {
                extent: result.extent.label,
                pct: fmtNum(Math.min(100, result.coverage.pct), 1),
                km2: fmtKm2(result.coverage.analysed_km2),
                resolution: result.resolution_m ? Math.round(result.resolution_m) : '—',
              })}
            </ResultNote>
            {(result.notes ?? []).map((note) => (
              <ResultNote key={note}>{t(`analysis.note.${note}`, note)}</ResultNote>
            ))}
          </>
        ) : null
      }
    >
      <label className="fld" htmlFor="chEpochA">
        {t('analysis.change.epochs')}
      </label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <select className="std" id="chEpochA" value={a} onChange={(e) => setEpochA(e.target.value)}>
          {epochs.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
          {epochs.length === 0 ? <option value="">{t('analysis.noLayerOption')}</option> : null}
        </select>
        <span aria-hidden="true">→</span>
        <select className="std" id="chEpochB" value={b} onChange={(e) => setEpochB(e.target.value)}>
          {epochs.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
          {epochs.length === 0 ? <option value="">{t('analysis.noLayerOption')}</option> : null}
        </select>
      </div>

      <label className="fld" htmlFor="chExt">
        {t('analysis.change.extent')}
      </label>
      <select
        className="std"
        id="chExt"
        value={extent}
        onChange={(e) => setExtent(e.target.value as 'scope' | 'aoi')}
      >
        <option value="scope">{scopeLabel}</option>
        {aoi ? <option value="aoi">{t('analysis.aoiName')}</option> : null}
      </select>

      <button
        type="button"
        className="btn primary ad-run"
        id="chRun"
        disabled={run.phase === 'running'}
        onClick={submit}
      >
        <Icon name="play" size={13} /> {t('analysis.change.run')}
      </button>
    </ToolFrame>
  );
}

export default Change;
